/**
 * account-page.tsx — Khu "Cài đặt cá nhân" (/account/:section), tách hẳn khỏi
 * Cài đặt hệ thống (/settings) theo mẫu SaleSmartly (/next/user/information).
 *
 * Mọi thứ ở đây là của RIÊNG người đang đăng nhập — hồ sơ, mật khẩu, giao diện,
 * phím tắt. Cấu hình dùng chung cho cả tổ chức nằm ở /settings.
 * Lối vào: menu tài khoản (ảnh đại diện ở đáy cột menu chính); điện thoại: ngăn Cá nhân (ảnh đại diện đầu Nhắn tin) → Cài đặt.
 */
import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SubnavPanel, subnavItemClass } from '@/components/shared/section-subnav'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { cn } from '@/lib/utils'
import { accountScreen } from './account-screen'
import { AccountMobileSettings } from './account-mobile-settings'
import { ProfileSection } from './profile-section'
import { PreferencesSection } from './preferences-section'
import { ShortcutsSection } from './shortcuts-section'
import { LoginHistorySection } from './login-history-section'
import { NotificationsSection } from './notifications-section'
import { ACCOUNT_MODULES_META, type AccountModuleMeta } from './account-modules'

/** Phần vẽ của từng mục — dữ liệu (nhãn, mô tả) ở account-modules.ts. */
const RENDER: Record<string, () => React.ReactNode> = {
  'profile': () => <ProfileSection />,
  'login-history': () => <LoginHistorySection />,
  'preferences': () => <PreferencesSection />,
  'notifications': () => <NotificationsSection />,
  'shortcuts': () => <ShortcutsSection />,
}

const MODULE_IDS = ACCOUNT_MODULES_META.map((m) => m.id)

export function AccountPage() {
  const { section } = useParams()
  const mobile = useIsMobile()
  const { t } = useTranslation(['shell', 'accountPages'])
  // `ACCOUNT_MODULES_META` dùng getter để đọc bản dịch mới nhất mỗi lần đọc `.label`/`.description` — tính LẠI
  // trong thân component (không phải hằng cấp module) để không "đóng băng" giá trị lúc i18next chưa tải xong
  // namespace `accountPages`, hoặc khi người dùng đổi ngôn ngữ sau đó.
  const modules = ACCOUNT_MODULES_META.map((m) => ({ ...m, render: RENDER[m.id] }))
  const screen = accountScreen(section, MODULE_IDS, mobile)
  if (screen.kind === 'redirect') return <Navigate to={screen.to} replace />
  if (screen.kind === 'list') return <AccountMobileSettings />
  const active = modules.find((m) => m.id === screen.section)!
  if (mobile) return <AccountMobileDetail module={active} />

  return (
    <div className="flex h-full min-h-0">
      <SubnavPanel title={t('userMenu.personalSettings')}>
        <nav className="space-y-0.5">
          {modules.map((m) => {
            const Icon = m.icon
            return (
              <Link
                key={m.id}
                to={`/account/${m.id}`}
                replace
                aria-current={m.id === active.id ? 'page' : undefined}
                className={cn(subnavItemClass(m.id === active.id), 'w-full')}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{m.label}</span>
              </Link>
            )
          })}
        </nav>
      </SubnavPanel>

      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto max-w-[1400px] space-y-5 px-6 py-5">
          <div>
            <h1 className="text-lg font-medium">{active.label}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{active.description}</p>
          </div>
          <section className="min-w-0">{active.render()}</section>
        </div>
      </div>
    </div>
  )
}

/** Điện thoại — màn 2: nội dung một mục, trọn màn hình; thanh trên có nút quay lại danh sách. */
function AccountMobileDetail({ module: m }: { module: AccountModuleMeta & { render: () => React.ReactNode } }) {
  const { t } = useTranslation('accountPages')
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center gap-1 border-b bg-background px-2 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))]">
        <Link to="/account" replace aria-label={t('accountPages:page.backToPersonalSettings')} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-accent">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold">{m.label}</h1>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="space-y-4 px-3 py-3">
          <p className="px-1 text-sm text-muted-foreground">{m.description}</p>
          <section className="min-w-0">{m.render()}</section>
        </div>
      </div>
    </div>
  )
}
